import { Divider, List, SearchInput, TITLE_TEXT, TockDialog } from 'omakase';
import { PatronLocationContainer } from 'components/patron-location/PatronLocationContainer';
import { PatronLocationGhostButton } from 'components/patron-location/PatronLocationGhostButton';
import {
  SEARCH_BAR_SECTION_DIVIDER,
  SearchBarOption,
  SearchBarOptionOrSectionDivider,
} from 'utils/searchBar';
import { Theme, css } from '@emotion/react';
import { consumer } from '@tock/coretypes';
import { inputContainer, list } from 'components/nav-search/searchBarCssDefs';
import {
  omniSearchContainer,
  slideContainer,
  structuredSearchContainer,
} from 'components/nav-search/searchBarCombinedCssDefs';
import { useDebouncedCallback } from 'use-debounce';
import React, { useEffect, useRef, useState } from 'react';
import SearchBarResult from 'components/nav-search/SearchBarResult';
import SearchLauncherWidget from 'consumer-page-widgets/SearchLauncherWidget';

const TRANSITION_DURATION = 225;

interface SearchBarProps {
  metroArea?: consumer.IMetroArea;
  currentSearch: string;
  setCurrentSearch(value: string): void;
  clearSearchResults(): void;
  onSearch(value: string): void;
  onSelectEntry(entry: SearchBarOption, idx: number): void;
  onClose(): void;
  placeholder: string;
  results: SearchBarOptionOrSectionDivider[];
  isDialogOpen: boolean;
  dataTestId?: string;
}

export const SearchBarCombined: React.FunctionComponent<SearchBarProps> = ({
  clearSearchResults,
  setCurrentSearch,
  onSelectEntry,
  results,
  onClose,
  placeholder,
  onSearch,
  isDialogOpen,
  dataTestId = 'searchnav-container',
  metroArea,
}) => {
  const [currentSearchLocal, setCurrentSearchLocal] = useState('');
  const [isOmnisearchVisible, setIsOmnisearchVisible] = useState(false);
  const [isStructuredSearchVisible, setIsStructuredSearchVisible] = useState(true);
  const { focusIndex, setFocusIndex, handleKeyDown } = useFocusToggleIndex(results);

  const textInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (focusIndex === -1) {
      textInputRef?.current?.focus();
    }
  }, [focusIndex]);

  useEffect(() => {
    setCurrentSearch(currentSearchLocal);
  }, [currentSearchLocal, setCurrentSearch]);

  useEffect(() => {
    return function cleanup() {
      clearSearchResults();
    };
  }, [clearSearchResults]);

  const debouncedCallback = useDebouncedCallback(onSearch, 200);

  const handleSearchChanged = (value: string) => {
    setCurrentSearchLocal(value);
    debouncedCallback(value);

    if (!value) {
      clearSearchResults();
    }
  };

  /* set visiblity:hidden on appropriate screen after transition duration to prevent keyboard
  tabbing into inactive screen UI */
  useEffect(() => {
    setIsStructuredSearchVisible(true);
    setIsOmnisearchVisible(true);
    const timer = setTimeout(() => {
      if (currentSearchLocal === '') {
        setIsStructuredSearchVisible(true);
        setIsOmnisearchVisible(false);
      } else {
        setIsStructuredSearchVisible(false);
        setIsOmnisearchVisible(true);
      }
    }, TRANSITION_DURATION);
    return () => clearTimeout(timer);
  }, [currentSearchLocal]);

  return (
    <TockDialog
      css={({ colors }: Theme) => css`
        background-color: ${colors.bgLighter};
      `}
      aria-labelledby={TITLE_TEXT}
      open={isDialogOpen}
      onClose={() => {
        setCurrentSearchLocal('');
        onClose();
      }}
      type="fullScreen"
      headingConfig={{
        right: { configType: 'closeButton' },
        center: {
          configType: 'button',
          button: <PatronLocationContainer initialComponent={PatronLocationGhostButton} />,
        },
      }}
      disablePadding
      data-testid={dataTestId}
    >
      <div css={inputContainer}>
        <SearchInput
          aria-label="Search"
          data-testid="business-search-input"
          placeholder={placeholder}
          value={currentSearchLocal}
          onChange={handleSearchChanged}
          onFocus={() => setFocusIndex(-1)}
          onKeyDown={handleKeyDown}
          ref={textInputRef}
          size="large"
          fullWidth
        />
        <Divider
          css={css`
            margin: 2rem 0 0 0;
          `}
        />
      </div>
      <div css={slideContainer(currentSearchLocal, TRANSITION_DURATION)}>
        <div css={structuredSearchContainer(currentSearchLocal, isStructuredSearchVisible)}>
          <SearchLauncherWidget metroArea={metroArea} />
        </div>
        <div
          css={(theme: Theme) =>
            omniSearchContainer(currentSearchLocal, isOmnisearchVisible, theme)
          }
        >
          <List css={list}>
            {results.map((entry, idx) => {
              if (entry === SEARCH_BAR_SECTION_DIVIDER) {
                return <Divider component="li" key={idx} position="top" variant="thick" tall />;
              }
              return (
                <SearchBarResult
                  entry={entry}
                  key={idx}
                  idx={idx}
                  focused={focusIndex === idx}
                  onClick={(entry) => onSelectEntry(entry, idx)}
                  onKeyDown={handleKeyDown}
                  setFocused={setFocusIndex}
                />
              );
            })}
          </List>
        </div>
      </div>
    </TockDialog>
  );
};

export default SearchBarCombined;

const useFocusToggleIndex = (results: SearchBarOptionOrSectionDivider[]) => {
  const [focusIndex, setFocusIndex] = React.useState(-1);

  function isSearchBarOption(result: SearchBarOptionOrSectionDivider): result is SearchBarOption {
    return result !== SEARCH_BAR_SECTION_DIVIDER;
  }

  function getUpdatedFocusIndexFromChange(
    results: SearchBarOptionOrSectionDivider[],
    focusIndex: number,
    amountToChange: 1 | -1
  ) {
    return Math.min(results.length - 1, Math.max(-1, focusIndex + amountToChange));
  }

  function goToNextFocusableElement(event: React.KeyboardEvent<HTMLElement>, direction: 1 | -1) {
    event.preventDefault();
    let amountToChange = direction;
    let updatedIndex = getUpdatedFocusIndexFromChange(results, focusIndex, amountToChange);
    let resultAtUpdatedIndex = results[updatedIndex];

    if (updatedIndex < 0) {
      setFocusIndex(updatedIndex);
    } else {
      while (
        resultAtUpdatedIndex === SEARCH_BAR_SECTION_DIVIDER ||
        (isSearchBarOption(resultAtUpdatedIndex) && resultAtUpdatedIndex.category === 'HEADING')
      ) {
        amountToChange += direction;
        updatedIndex = Math.min(results.length - 1, Math.max(-1, focusIndex + amountToChange));
        resultAtUpdatedIndex = results[updatedIndex];
      }

      setFocusIndex(updatedIndex);
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        goToNextFocusableElement(event, 1);
        break;
      case 'ArrowUp':
        goToNextFocusableElement(event, -1);
        break;
      case 'Tab':
      case 'Enter':
      case 'Escape':
      case 'Shift':
        return;
      default:
        setFocusIndex(-1);
    }
  };
  return { focusIndex, setFocusIndex, handleKeyDown };
};
